import React, {
  useState,
  useEffect,
  useMemo,
} from "react";

import axios from "axios";
import { useAuth } from "../Context/AuthContext";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import {
  FaUsers,
  FaBuilding,
  FaFileExport,
  FaTimes,
} from "react-icons/fa";

import { Base_URL } from "../url";

import "../Styling/LeaveBalanceNew.css";

const LeaveBalancePageNew = () => {
    const {user} = useAuth();
      const orgId = user?.organisationId;

  const [employees, setEmployees] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [searchQuery, setSearchQuery] =
    useState("");

  const [companyFilter, setCompanyFilter] =
    useState("");
    const [allCompanies, setAllCompanies] = useState([]);

  /*
  ==========================
  HELPERS
  ==========================
  */

  const getInitials = (name) => {
    if (!name) return "?";

    const words = name.split(" ");

    if (words.length === 1)
      return words[0]
        .substring(0, 2)
        .toUpperCase();

    return (
      words[0][0] +
      words[words.length - 1][0]
    ).toUpperCase();
  };

  const getBalanceClass = (
    balance
  ) => {
    const value = Number(balance);

    if (value === 0)
      return "danger";

    if (value < 2)
      return "warning";

    if (value <= 10)
      return "primary";

    return "success";
  };

  /*
  ==========================
  API
  ==========================
  */

  const fetchData = async () => {
    try {
      setLoading(true);

      const response =
        await axios.get(
          `${Base_URL}/Leave/LeaveBalanceAll-new`,
          {
            params: {
     orgId,

              companyCode:
                companyFilter || null,
            },
          }
        );

      setEmployees(
        response.data
      );
if (!companyFilter) {
      const companies = [
        ...new Set(response.data.map((x) => x.companyCode)),
      ];
      setAllCompanies(companies);
    }

    setError("");
  } catch (err) {
    console.log(err);
    setError("Unable to load leave balances.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [
    user,
    companyFilter,
  ]);

  /*
  ==========================
  STATS
  ==========================
  */

  const stats = useMemo(() => {
    const companies = [
      ...new Set(
        employees.map(
          (x) => x.companyCode
        )
      ),
    ];

    const leaveTypes = [
      ...new Set(
        employees.flatMap(
          (x) =>
            x.leaveBalances.map(
              (y) =>
                y.description
            )
        )
      ),
    ];

    const totalBalance =
      employees.reduce(
        (sum, emp) =>
          sum +
          emp.leaveBalances.reduce(
            (a, b) =>
              a +
              Number(
                b.balance
              ),
            0
          ),
        0
      );

    return {
      employees:
        employees.length,

      companies:
        companies.length,

      leaveTypes:
        leaveTypes.length,

      totalBalance,
    };
  }, [employees]);

  /*
  ==========================
  COMPANY LIST
  ==========================
  */

  const companyList =
    useMemo(() => {
      return [
        ...new Set(
          employees.map(
            (x) =>
              x.companyCode
          )
        ),
      ];
    }, [employees]);

  /*
  ==========================
  SEARCH
  ==========================
  */

  const filteredEmployees =
    useMemo(() => {
      let data = [
        ...employees,
      ];

      if (
        searchQuery.trim()
      ) {
        const query =
          searchQuery.toLowerCase();

        data = data.filter(
          (emp) =>
            emp.employee
              ?.toLowerCase()
              .includes(
                query
              ) ||
            String(
              emp.resourceId
            )
              .toLowerCase()
              .includes(
                query
              )
        );
      }

      return data;
    }, [
      employees,
      searchQuery,
    ]);

  /*
  ==========================
  SORT
  ==========================
  */

  const sortedEmployees =
    useMemo(() => {
      return [
        ...filteredEmployees,
      ].sort(
        (a, b) =>
          a.employee.localeCompare(
            b.employee
          )
      );
    }, [
      filteredEmployees,
    ]);

  /*
  ==========================
  CLEAR
  ==========================
  */

  const clearFilters =
    () => {
      setSearchQuery("");

      setCompanyFilter("");
    };

  /*
  ==========================
  EXCEL
  ==========================
  */

  const downloadExcel =
    () => {
      const rows = [];

      sortedEmployees.forEach(
        (employee) => {
          employee.leaveBalances.forEach(
            (
              leave
            ) => {
              rows.push({
                Employee:
                  employee.employee,

                ResourceId:
                  employee.resourceId,

                Organisation:
                  employee.organisationId,

                Company:
                  employee.companyCode,

                LeaveType:
                  leave.description,

                Balance:
                  leave.balance,
              });
            }
          );
        }
      );

      const worksheet =
        XLSX.utils.json_to_sheet(
          rows
        );

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "LeaveBalances"
      );

      const excelBuffer =
        XLSX.write(
          workbook,
          {
            bookType:
              "xlsx",
            type:
              "array",
          }
        );

      const file =
        new Blob(
          [excelBuffer],
          {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }
        );

      saveAs(
        file,
        `LeaveBalance_${
          new Date()
            .toISOString()
            .split("T")[0]
        }.xlsx`
      );
    };

  /*
  ==========================
  TOTAL
  ==========================
  */

  const totalLeaveBalance =
    (employee) => {
      return employee.leaveBalances.reduce(
        (
          sum,
          item
        ) =>
          sum +
          Number(
            item.balance
          ),
        0
      );
    };

  /*
  ==========================
  LOADING
  ==========================
  */

  if (loading) {
    return (
      <div className="lb-loading">
        <div className="lb-spinner"></div>

        <p>
          Loading leave
          balances...
        </p>
      </div>
    );
  }

  /*
  ==========================
  ERROR
  ==========================
  */

  if (error) {
    return (
      <div className="lb-empty-state">
        {error}
      </div>
    );
  }
  /*
==========================
RETURN
==========================
*/

return (
  <div className="lb-container">
    {/* HEADER */}

    <div className="lb-header">
      <div>
        <h1 className="lb-title">
          Leave Balance Report
        </h1>

        <p className="lb-subtitle">
          Current leave balances across employees
        </p>
      </div>

      <div className="lb-controls">
        {/* SEARCH */}

        <div className="lb-search">
          <input
            className="lb-search-input"
            type="text"
            placeholder="Search Employee / Resource ID"
            value={searchQuery}
            onChange={(e) =>
              setSearchQuery(
                e.target.value
              )
            }
          />

          {searchQuery && (
            <button
              className="lb-search-clear"
              onClick={() =>
                setSearchQuery("")
              }
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* COMPANY */}

        <div className="lb-company-selector">
          <label>
            Company
          </label>

          <select
            className="lb-company-dropdown"
            value={
              companyFilter
            }
            onChange={(e) =>
              setCompanyFilter(
                e.target.value
              )
            }
          >
            <option value="">
              All
            </option>

          {allCompanies.map((company) => (
  <option key={company} value={company}>
    {company}
  </option>
))}
          </select>
        </div>

        {/* EXPORT */}

        <button
          className="lb-export-btn"
          onClick={
            downloadExcel
          }
        >
          <FaFileExport />

          Export Excel
        </button>

        {/* CLEAR */}

        <button
          className="lb-clear-search-btn"
          onClick={
            clearFilters
          }
        >
          Clear
        </button>
      </div>
    </div>

    {/* STATS */}

    <div className="lb-stats">
      <div className="lb-stat-card">
        <FaUsers />

        <span className="lb-stat-value">
          {
            stats.employees
          }
        </span>

        <span className="lb-stat-label">
          Employees
        </span>
      </div>

      <div className="lb-stat-card">
        <FaBuilding />

        <span className="lb-stat-value">
          {
            stats.companies
          }
        </span>

        <span className="lb-stat-label">
          Companies
        </span>
      </div>

      <div className="lb-stat-card">
        <span className="lb-stat-value">
          {
            stats.leaveTypes
          }
        </span>

        <span className="lb-stat-label">
          Leave Types
        </span>
      </div>


    </div>

    {/* EMPTY */}

    {sortedEmployees.length ===
    0 ? (
      <div className="lb-empty-state">
        No leave balances
        match your search
        criteria.
      </div>
    ) : (
      <div className="lb-employee-list">
        {sortedEmployees.map(
          (
            employee
          ) => (
            <div
              className="lb-employee-card"
              key={
                employee.resourceId
              }
            >
              {/* HEADER */}

              <div className="lb-employee-header">
                <div className="lb-user">
                  <div className="lb-avatar">
                    {getInitials(
                      employee.employee
                    )}
                  </div>

                  <div>
                    <h2 className="lb-employee-name">
                      {
                        employee.employee
                      }
                    </h2>

                    <div className="lb-employee-meta">
                      <span className="lb-badge">
                        {
                          employee.resourceId
                        }
                      </span>

                      <span className="lb-badge">
                        Org{" "}
                        {
                          employee.organisationId
                        }
                      </span>

                      <span className="lb-badge">
                        Company{" "}
                        {
                          employee.companyCode
                        }
                      </span>
                    </div>
                  </div>
                </div>


              </div>

              {/* LEAVES */}

              <div className="lb-chip-grid">
                {employee.leaveBalances.map(
                  (
                    leave
                  ) => (
                    <div
                      key={
                        leave.leaveTypeId
                      }
                      className={`lb-chip ${getBalanceClass(
                        leave.balance
                      )}`}
                    >
                      <div className="lb-chip-title">
                        {
                          leave.description
                        }
                      </div>

                      <div className="lb-chip-value">
                        {Number(
                          leave.balance
                        ).toFixed(
                          1
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )
        )}
      </div>
    )}
  </div>
);

};

export default LeaveBalancePageNew;